import { useApi, useToaster } from "@shopexperts/shared";
import { useInfiniteQuery } from "@tanstack/vue-query";
import { computed, type Ref, toValue } from "vue";

interface UseAdminBadgesOptions {
  search?: Ref<string> | string;
  status?: 'active' | 'inactive';
  tag?: Ref<string> | string;
  limit?: number;
}

export function useAdminBadges(options: UseAdminBadgesOptions = {}) {
  const api = useApi();
  const toaster = useToaster();
  
  const { search = '', status, tag = '', limit = 20 } = options;
  
  // Convert search to computed value that works with both refs and strings
  const searchValue = computed(() => toValue(search));
  const tagValue = computed(() => toValue(tag));

  const {
    data,
    isLoading,
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: computed(() => ['admin', 'badges', { search: searchValue.value, status, tag: tagValue.value }]),
    queryFn: async ({ pageParam }) => {
      return await api.admin.badges.listBadges({
        query: {
          limit,
          cursor: pageParam,
          search: searchValue.value || undefined,
          status,
          tag: tagValue.value || undefined,
        },
      });
    },
    getNextPageParam: (lastPage) => {
      return lastPage.pagination.has_next ? lastPage.pagination.cursor : undefined;
    },
    initialPageParam: undefined as string | undefined,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const badges = computed(() => {
    return data.value?.pages.flatMap(page => page.data) || [];
  });

  if (error.value) {
    toaster.create({
      title: "Failed to load badges",
      type: "error",
    });
    console.error('[useAdminBadges] Error:', error.value);
  }

  return {
    badges,
    isLoading,
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  };
}

export default useAdminBadges;

